import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {

    constructor(props: Props) {

        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {

        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {

        // surface to the dev console; in prod this is the only signal we have
        console.error("Admin SPA error boundary caught", error, info);
    }

    private handleReload = () => {

        window.location.reload();
    };

    render() {

        if (!this.state.hasError)
            return this.props.children;

        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-6">
                <div className="w-full max-w-md p-6 rounded-lg border bg-card text-card-foreground space-y-4">
                    <div>
                        <h1 className="text-xl font-semibold">Something went wrong</h1>
                        <p className="text-sm text-muted-foreground mt-1">An unexpected error occurred. Reloading usually fixes it.</p>
                    </div>
                    {this.state.error?.message && (
                        <p className="text-xs font-mono bg-muted p-2 rounded text-muted-foreground break-all">
                            {this.state.error.message}
                        </p>
                    )}
                    <Button onClick={this.handleReload} className="w-full">Reload</Button>
                </div>
            </div>
        );
    }
}
